import { ColorSwatch, IconImage } from 'libs/searchspring/components/collection/filters/FilterCustomisations'
import { FilterCollapse } from 'libs/searchspring/components/collection/filters/FilterCollapse'
import { FilterInputSlider } from 'libs/searchspring/components/collection/filters/FilterSlider'
import { UniversalSizeFilter } from 'libs/searchspring/components/collection/filters/UniversalSizeFilter'
import { useFetchedFilter, useFilterCustomizations, useFilterValues, useActiveFilterLabel } from 'libs/searchspring/components/collection/filters/FilterHooks'
import { isInStockSizesField, shouldShowInStockSizesField } from 'libs/searchspring/components/collection/filters/SizeFilterUtils'
import clsx from 'clsx'

export function FilterItem({ filter, activeFilters, fetchedFilters, onFilter, onRemove }) {
  if (!filter) return null

  const fetchedFilter = useFetchedFilter(filter, fetchedFilters)
  const filterCustomizations = useFilterCustomizations(filter.field)
  const values = useFilterValues(filter, activeFilters, fetchedFilter, filterCustomizations)
  const activeFilterLabel = useActiveFilterLabel(filter, values, fetchedFilter, activeFilters)

  if (!shouldShowInStockSizesField(filter.field)) return null

  const isSizeFilter =
    filter.field === 'variant_mfield_utopia_size_label' ||
    filter.field === 'filter.v.option.size' ||
    filter.field === 'size' ||
    isInStockSizesField(filter.field) ||
    filter.label?.toLowerCase() === 'size' ||
    values.some((value) => value.label?.includes(','))

  const FilterContent = () => {
    if (isSizeFilter) {
      return <UniversalSizeFilter values={values} onFilter={onFilter} />
    }

    switch (filter.type) {
      case 'slider':
        return (
          <FilterInputSlider
            filter={filter}
            fetchedFilter={fetchedFilter}
            activeFilters={activeFilters}
            onFilter={onFilter}
            onRemove={onRemove}
          />
        )
      case 'list':
        if (!values.length) return null
        return <FilterInputs field={filter.field} values={values} onFilter={onFilter} />
      default:
        if (!values.length) return null
        return <FilterInputs field={filter.field} values={values} onFilter={onFilter} />
    }
  }

  const layoutClass = isSizeFilter
    ? 'flex flex-col gap-y-1.5'
    : values.length > 6
      ? 'grid grid-cols-2 gap-x-0.5 gap-y-1'
      : 'flex flex-col gap-y-1.5'

  return (
    <FilterCollapse
      label={filter.label}
      activeFilterLabel={activeFilterLabel}
      valueType={filter.type === 'slider' ? undefined : 'number'}
    >
      <div className={`${layoutClass}`}>{FilterContent()}</div>
    </FilterCollapse>
  )
}

const FilterInputs = ({ values, onFilter, field }) => {
  return (
    <>
      {values.map((item) => {
        return (
          <div className="group/filter-item relative flex w-fit cursor-pointer items-start gap-x-0.5" key={item.value}>
            <input
              id={item.value}
              className={clsx(
                'peer field-checkbox shrink-0 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 group-hover/filter-item:border-primary group-hover/filter-item:bg-primary',
                item.color || item.icon
                  ? 'overflow-hidden absolute z-2 !size-1.5 rounded-full opacity-0 inset-tl'
                  : 'mt-0.125'
              )}
              type="checkbox"
              name={field}
              checked={item.checked}
              disabled={item.disabled}
              onClick={() => onFilter(item.value)}
              aria-label={item.label}
            />
            <label
              for={item.value}
              className="flex grow cursor-pointer items-start peer-disabled:pointer-events-none peer-disabled:cursor-not-allowed peer-disabled:opacity-50 peer-checked:[&_.swatch-inner]:outline-brand-black"
            >
              {item.color && <ColorSwatch color={item.color} />}
              {item.icon && <IconImage iconObject={item.icon} />}
              <span className="paragraph-xs">
                {item.label}
                <span className="paragraph-xs text-type-black-secondary">&nbsp;({item.count ?? 0})</span>
              </span>
            </label>
          </div>
        )
      })}
    </>
  )
}
